export type Role = 'admin' | 'member';

// Hard access boundary (see genba-api-spec.md's Teams section /
// internal/models/team.go): every project and every member user has one;
// admins have none (team is null). Teams grant nothing by themselves — a
// user still needs a per-project grant — they just cap which projects a
// grant/primary-project may ever point at.
export type Team = 'fullstack' | 'embedded' | 'networking';
export const TEAMS: Team[] = ['fullstack', 'embedded', 'networking'];

export interface Project {
  id: number;
  name: string;
  slug: string;
  status: string;
  team: Team;
  prod_url: string;
  repo: string;
  prefix: string;
  // Optional hardware/ERP cross-references + advisory WIP limits — see
  // genba-api-spec.md's Project model (RefERP/RefArtifact/RefHWRev/WIP).
  ref_erp?: string;
  ref_artifact?: string;
  ref_hw_rev?: string;
  wip?: Partial<Record<'in-progress' | 'review', number>>;
  created_at: string;
  updated_at: string;
}

export interface Me {
  user: {
    id: number;
    email: string;
    name: string;
    role: Role;
    project_id: number | null;
    totp_enabled: boolean;
  };
  projects: Project[];
}

export interface Paginated<T> {
  results: T[];
  count: number;
  page: number;
  per_page: number;
}

export const TASK_COLUMNS = [
  { id: 'backlog', name: 'Backlog', kind: 'gray' as const, sw: '#9AA0A6' },
  { id: 'in-progress', name: 'In progress', kind: 'teal' as const, sw: '#1F6570' },
  { id: 'review', name: 'Review', kind: 'warn' as const, sw: '#A9700A' },
  { id: 'done', name: 'Done', kind: 'lime' as const, sw: '#7AB000' }
] as const;
export type TaskCol = (typeof TASK_COLUMNS)[number]['id'];

export interface Task {
  id: number;
  project_id: number;
  key: string;
  col: TaskCol | string;
  title: string;
  tag: string;
  desc: string;
  assignee_user_id: number | null;
  from_ticket_id: number | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export type TicketStatus = 'open' | 'triaged' | 'in-progress' | 'resolved' | 'closed';

export interface Ticket {
  id: number;
  project_id: number | null;
  title: string;
  desc: string;
  status: TicketStatus | string;
  assignee_user_id: number | null;
  // Redaction (see genba-api-spec.md "HARD RULE (isolation)"): non-admin
  // callers never receive src_company/src_property — the keys are omitted
  // from the JSON entirely, not just blanked, hence optional here.
  src_company?: string;
  src_property?: string;
  src_room: string;
  src_site: string;
  crm_issue_id: number | null;
  crm_url: string;
  deploy_sha: string;
  created_at: string;
  updated_at: string;
}

export interface TicketComment {
  id: number;
  ticket_id: number;
  author_user_id: number | null;
  body: string;
  created_at: string;
}

export interface Deploy {
  id: number;
  project_id: number;
  sha: string;
  target: string;
  by: string;
  artifact: string;
  created_at: string;
}

export interface DebtEntry {
  id: number;
  project_id: number;
  desc: string;
  severity: 'low' | 'medium' | 'high' | string;
  ceiling: string;
  resolved: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface Doc {
  id: number;
  project_id: number;
  title: string;
  body: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface Activity {
  id: number;
  project_id: number;
  actor_user_id: number | null;
  type: string;
  text: string;
  created_at: string;
}

// GET /api/admin/activity/daily?date=YYYY-MM-DD — the admin-only "who did
// what on that day" digest. Cross-project by definition, so it has no
// member-facing counterpart (see internal/api/handlers/admin_activity.go).
export interface DailyActivityItem {
  id: number;
  type: string;
  text: string;
  created_at: string;
  project: { id: number; name: string };
}

export interface DailyActivityGroup {
  // null for the System group: activities with no actor (token-authed
  // intake) plus any whose actor account has since been deleted.
  user_id: number | null;
  name: string;
  email: string;
  count: number;
  activities: DailyActivityItem[];
}

export interface DailyActivity {
  date: string;
  timezone: string;
  count: number;
  // True only if the day blew past the server's per-day row cap — surfaced
  // rather than silently showing a partial day as a complete one.
  truncated: boolean;
  groups: DailyActivityGroup[];
}

// From GET /api/projects/:id/members — deliberately just id+name (the sole
// user-listing endpoint members can reach).
export interface AssignableUser {
  id: number;
  name: string;
}

export interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: Role;
  project_id: number | null;
  team: Team | null;
  projects: number[];
  totp_enabled: boolean;
  last_active: string | null;
  created_at: string;
  temp_password?: string;
}

export interface LoginEvent {
  id: number;
  user_id: number;
  created_at: string;
  ip_address: string;
  device: string;
  browser: string;
  os: string;
  // Only present on the global recent-logins feed, not the per-user one.
  email?: string;
}

export interface SearchResults {
  tasks: Array<Pick<Task, 'id' | 'project_id' | 'key' | 'title'>>;
  tickets: Array<Pick<Ticket, 'id' | 'project_id' | 'title'>>;
}
